import { useQuery } from '@tanstack/react-query';
import { Workorder } from '@certhon/models';
import { useCurrentUser } from '@/features/users/users.hooks';
import { dayPlanningQueries } from '../dayplanning/dayplanning.queries';
import {
  sortWorkorders,
  useGeocodedCoordinates,
} from '../dayplanning/dayplanning.hooks';

/**
 * Fetch workorders for a specific day and user
 * If userId is null, uses current logged-in user
 */
export function useMyDayWorkorders(userId: number | null, date: string) {
  const currentUser = useCurrentUser();
  const effectiveUserId = userId ?? currentUser?.recnum ?? 0;

  return useQuery({
    ...dayPlanningQueries.day(effectiveUserId, date),
    enabled: effectiveUserId > 0,
  });
}

/**
 * Fetch routes between workorders
 * Uses techRoutes query to share cache with day planning view
 */
export function useRoutes(workorders: Workorder[] | undefined) {
  const currentUser = useCurrentUser();

  // Determine which tech ID to use for caching
  // Priority: first workorder's assigned user > current user
  const effectiveTechId =
    workorders?.[0]?.assigned_user_id ?? currentUser?.recnum ?? 0;

  // Always use techRoutes for better cache sharing
  return useQuery({
    ...dayPlanningQueries.techRoutes(effectiveTechId, workorders ?? []),
    enabled: !!workorders && workorders.length > 0 && effectiveTechId > 0,
    throwOnError: false,
  });
}

// Re-export utilities
export { sortWorkorders, useGeocodedCoordinates };
